import React, { useEffect, useRef, useState } from 'react'
import {
  EVENTO,
  OPCOES_CARGO,
  OPCOES_EQUIPE,
  OPCOES_LOCACAO,
  TEXTO_ALMOCO,
  CARDAPIO,
  TEXTO_LGPD,
  VERSAO_LGPD,
  VERSAO_IMAGEM_VOZ,
} from '../../dados/evento'
import TermosImagem from '../TermosImagem'

/** Talher — Tabler Icons (tools-kitchen-2), a mesma família dos outros. */
function IconeTalher(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" focusable="false" {...props}>
      <path d="M19 3v12h-5c-.023 -3.681 .184 -7.406 5 -12zm0 12v6h-1v-3m-10 -14v17m-3 -17v3a3 3 0 1 0 6 0v-3" />
    </svg>
  )
}
import { lerUtm } from '../../lib/utm'
import { conversaoInscricao } from '../../lib/rastreamento'

const ENDPOINT = '/_form/inscricao/'

const VAZIO = {
  nome: '',
  email: '',
  whatsapp: '',
  imobiliaria: '',
  cargo: '',
  equipe: '',
  locacao: '',
  cpf: '',
  almoco: '',
  lgpd: false,
  imagemVoz: false,
}

/**
 * Máscara de WhatsApp. Formata enquanto digita e nunca deixa passar mais de 11
 * dígitos — celular brasileiro com DDD.
 */
function mascararTelefone(valor) {
  const d = valor.replace(/\D/g, '').slice(0, 11)
  if (d.length <= 2) return d
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
}

/**
 * DDD brasileiro válido vai de 11 a 99, e o número tem 10 ou 11 dígitos.
 * A checagem existe porque o WhatsApp é o canal de contato do evento: um
 * número errado aqui é um inscrito que não recebe o ingresso.
 */
function telefoneValido(valor) {
  const d = valor.replace(/\D/g, '')
  if (d.length !== 10 && d.length !== 11) return false
  const ddd = Number(d.slice(0, 2))
  return ddd >= 11 && ddd <= 99
}

/** Máscara de CPF: 000.000.000-00, travada em 11 dígitos. */
function mascararCpf(valor) {
  const d = valor.replace(/\D/g, '').slice(0, 11)
  if (d.length <= 3) return d
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`
}

/**
 * CPF válido de verdade: DÍGITOS VERIFICADORES, não só contagem de caracteres.
 *
 * Checar só o tamanho deixa passar 000.000.000-00 e 123.456.789-00, que é
 * justamente o que alguém digita para pular o campo. Como o CPF aqui serve
 * para o credenciamento na porta (a lista é conferida por documento), um CPF
 * inválido no banco vira uma pessoa parada na fila no dia do evento.
 *
 * A regra: os dois últimos dígitos são calculados a partir dos nove primeiros
 * por soma ponderada módulo 11. Sequências de dígito repetido passam na conta
 * e são rejeitadas à parte — é o furo clássico do algoritmo.
 */
function cpfValido(valor) {
  const d = valor.replace(/\D/g, '')
  if (d.length !== 11) return false
  if (/^(\d)\1{10}$/.test(d)) return false

  const digito = (ate) => {
    let soma = 0
    for (let i = 0; i < ate; i += 1) soma += Number(d[i]) * (ate + 1 - i)
    const resto = (soma * 10) % 11
    return resto === 10 ? 0 : resto
  }

  return digito(9) === Number(d[9]) && digito(10) === Number(d[10])
}

function emailValido(valor) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(valor.trim())
}

/* ⚠️ VALIDAÇÃO VOLTOU A SER UMA SÓ (13/08). Ela tinha sido partida em duas
   quando o almoço virou modal — `validarPrincipal` para o que estava visível e
   `validarAlmoco` para o campo de dentro da caixa. Com a pergunta de volta ao
   formulário, tudo é visível e a divisão só criaria a chance de um campo ficar
   sem checagem.

   `validarAlmoco` continua exportada logo abaixo, sem consumidor, porque a
   pergunta pode voltar ao modal num pedido futuro — foi o que aconteceu duas
   vezes em dois dias. */
/**
 * Mensagem por campo. Serve para dois casos: a validação local logo abaixo,
 * e a tradução do array `campos` que o servidor devolve num 400 (ver
 * `enviar`) — o mesmo texto para o mesmo campo nos dois lugares, e um
 * fallback só se o servidor citar um campo que este dicionário não conhece.
 */
const MENSAGENS_CAMPO = {
  almoco: 'Informe se deseja o almoço.',
  nome: 'Informe seu nome completo.',
  email: 'Informe um e-mail válido.',
  whatsapp: 'Informe o WhatsApp com DDD.',
  cpf: 'Informe um CPF válido.',
  imobiliaria: 'Informe o nome da imobiliária.',
  cargo: 'Selecione seu cargo.',
  equipe: 'Selecione o tamanho da equipe.',
  locacao: 'Selecione uma opção.',
  lgpd: 'É necessário aceitar para concluir a inscrição.',
  imagemVoz: 'É necessário aceitar para concluir a inscrição.',
}

function validarPrincipal(dados) {
  const erros = {}
  if (!dados.almoco) erros.almoco = MENSAGENS_CAMPO.almoco
  if (!dados.nome.trim() || dados.nome.trim().length < 3) erros.nome = MENSAGENS_CAMPO.nome
  if (!emailValido(dados.email)) erros.email = MENSAGENS_CAMPO.email
  if (!telefoneValido(dados.whatsapp)) erros.whatsapp = MENSAGENS_CAMPO.whatsapp
  if (!cpfValido(dados.cpf)) erros.cpf = MENSAGENS_CAMPO.cpf
  if (!dados.imobiliaria.trim()) erros.imobiliaria = MENSAGENS_CAMPO.imobiliaria
  if (!dados.cargo) erros.cargo = MENSAGENS_CAMPO.cargo
  if (!dados.equipe) erros.equipe = MENSAGENS_CAMPO.equipe
  if (!dados.locacao) erros.locacao = MENSAGENS_CAMPO.locacao
  if (!dados.lgpd) erros.lgpd = MENSAGENS_CAMPO.lgpd
  if (!dados.imagemVoz) erros.imagemVoz = MENSAGENS_CAMPO.imagemVoz
  return erros
}

function validarAlmoco(dados) {
  const erros = {}
  if (!dados.almoco) erros.almoco = 'Informe se deseja o almoço.'
  return erros
}

/**
 * 5ª dobra — formulário de inscrição. Destino de todos os CTAs da página.
 *
 * O POST vai para /_form/inscricao/, servido pelo painel deste projeto, e a
 * inscrição termina ali: a tabela `inscricoes` é o destino ÚNICO. Este
 * formulário não alimenta a API de leads comercial — a lista do evento é
 * gerenciada no admin do próprio site e sai em CSV para a conferência na porta.
 *
 * UMA ETAPA SÓ, de novo (13/08). O fluxo foi de página única para duas etapas
 * (10/08, almoço no modal), voltou para uma (12/08), voltou para duas (12/08,
 * pedido do cliente) e volta agora para uma — a pergunta do almoço mora no
 * formulário e "Garantir minha presença" ENVIA.
 *
 * O que sobreviveu a todas as idas e vindas, e não deve ser mexido sem motivo:
 * a resposta do almoço é OBRIGATÓRIA (sim ou não) mesmo o almoço sendo
 * opcional. Sem resposta explícita a cozinha não tem número para comprar, e
 * "não respondeu" viraria "talvez" na planilha do dia.
 *
 * O ESTADO NUNCA É LIMPO EM ERRO. Quem preencheu dez campos e levou uma falha
 * de rede não pode reencontrar o formulário vazio: é aí que a pessoa desiste.
 * `setDados(VAZIO)` só acontece depois de resposta de sucesso.
 *
 * O evento de conversão dispara no sucesso da resposta, nunca no clique do
 * botão. Contar clique como conversão infla o número e envenena a otimização
 * das campanhas que o briefing exige.
 */
export default function SecaoInscricao({
  chamada = 'Garanta seu ingresso gratuito agora mesmo!',
  /* "garanta sua vaga antecipada ao evento" — regência trocada: a vaga não é
     "ao evento", é "no evento", e a antecipação é do ato, não da vaga. */
  subtitulo = 'Preencha os campos abaixo e garanta a sua vaga no evento.',
  textoAlmoco = TEXTO_ALMOCO,
  cardapio = CARDAPIO,
  textoLgpd = TEXTO_LGPD,
  textoCta = 'Garantir minha presença',
}) {
  const [dados, setDados] = useState(VAZIO)
  const [erros, setErros] = useState({})
  const [estado, setEstado] = useState('parado') // parado | enviando | sucesso | erro
  const [mensagemErro, setMensagemErro] = useState('')
  /* Se a arte do Nauta não carregar, cai no anel com tique. Isto não é
     paranoia: um <img> decorativo que falha desenha a moldura de imagem
     quebrada do navegador, e esta é a TELA DE CONFIRMAÇÃO — o pior lugar da
     página para exibir defeito. Um hipo de CDN não pode custar isso. */
  const [mascoteFalhou, setMascoteFalhou] = useState(false)
  const [termosAbertos, setTermosAbertos] = useState(false)
  /* O almoço voltou a ser SEGUNDA ETAPA em 12/08, a pedido do cliente. Ver o
     docblock do componente. */
  const botaoEnviarRef = useRef(null)

  const mudar = (campo) => (evento) => {
    const alvo = evento.target
    const valor =
      alvo.type === 'checkbox'
        ? alvo.checked
        : campo === 'whatsapp'
          ? mascararTelefone(alvo.value)
          : campo === 'cpf'
            ? mascararCpf(alvo.value)
            : alvo.value
    setDados((atual) => ({ ...atual, [campo]: valor }))
    setErros((atual) => {
      if (!atual[campo]) return atual
      const proximo = { ...atual }
      delete proximo[campo]
      return proximo
    })
  }

  /* ENVIO DIRETO (13/08). Era um handler de duas etapas — `abrirAlmoco`
     validava e abria o modal, `confirmarInscricao` enviava. Com a pergunta do
     almoço de volta ao formulário, o modal saiu e sobrou um caminho só. */
  async function enviar(evento) {
    evento.preventDefault()

    const achados = validarPrincipal(dados)
    setErros(achados)
    if (Object.keys(achados).length > 0) {
      // Leva o foco ao primeiro campo com erro: numa lista de dez campos, uma
      // mensagem no rodapé do formulário passa despercebida.
      const primeiro = document.querySelector('[data-erro="true"]')
      primeiro?.focus()
      return
    }

    setEstado('enviando')
    setMensagemErro('')

    try {
      const resposta = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...dados,
          // +55 nunca é digitado (ver campo-tel acima), mas viaja com o envio:
          // é o que faz o número gravado já servir pra WhatsApp/CSV sem
          // depender de alguém completar o código do país depois.
          whatsapp: `+55 ${dados.whatsapp}`,
          almoco: dados.almoco === 'sim',
          lgpdTextoVersao: VERSAO_LGPD,
          imagemVozTextoVersao: VERSAO_IMAGEM_VOZ,
          utm: lerUtm(),
        }),
      })

      if (!resposta.ok) {
        const corpo = await resposta.json().catch(() => ({}))

        /* 400 vem com `campos`: os nomes que reprovaram a validação do
           servidor (a mesma checagem que `validarPrincipal` faz no cliente,
           repetida lá porque o cliente pode ser contornado com um curl — ver
           o comentário em admin/lib/form.js). Sem isto, um valor que o
           cliente aceita mas o servidor rejeita (ex.: um select dessincronizado
           da allowlist do backend) cai no catch genérico e a pessoa só lê
           "Dados inválidos", sem saber qual campo corrigir. */
        if (resposta.status === 400 && Array.isArray(corpo.campos) && corpo.campos.length > 0) {
          const mapeados = {}
          for (const campo of corpo.campos) {
            mapeados[campo] = MENSAGENS_CAMPO[campo] || 'Verifique este campo.'
          }
          setErros(mapeados)
          setEstado('erro')
          setMensagemErro('Verifique os campos destacados abaixo.')
          const primeiro = document.querySelector('[data-erro="true"]')
          primeiro?.focus()
          return
        }

        throw new Error(corpo.erro || `Falha no envio (HTTP ${resposta.status})`)
      }

      // Só aqui a conversão é real.
      conversaoInscricao()
      setEstado('sucesso')
      setDados(VAZIO)
    } catch (e) {
      setEstado('erro')
      setMensagemErro(
        e.message ||
          'Não foi possível enviar agora. Tente de novo em instantes — seus dados continuam preenchidos.'
      )
    }
  }

  if (estado === 'sucesso') {
    return (
      <section className="secao secao-inscricao dia dobra-clara" id="inscricao">
        <div className="container-site">
          <div className="inscricao-sucesso" role="status">
            {/* O Nauta comemorando, no lugar do anel com tique que havia antes:
                é a mesma função (marco de "missão cumprida") dita pela
                personagem da identidade. Decorativo — a mensagem abaixo já diz
                tudo a quem usa leitor de tela, daí `alt=""`.

                Cai no anel de sempre em DOIS casos: campo vazio (é editável no
                Builder) ou a imagem falhando ao carregar (ver
                `mascoteFalhou`). */}
            {EVENTO.sucessoMascote && !mascoteFalhou ? (
              <img
                className="inscricao-sucesso-mascote"
                src={EVENTO.sucessoMascote}
                alt=""
                width="420"
                height="545"
                /* `eager`, não `lazy`: quando este bloco monta ele já está na
                   viewport (o formulário acabou de sair do lugar dele), então
                   o lazy só adiaria a decodificação do elemento que anuncia a
                   conversão. */
                loading="eager"
                onError={() => setMascoteFalhou(true)}
              />
            ) : (
              <span className="inscricao-sucesso-anel" aria-hidden="true">
                <span className="tique" />
              </span>
            )}
            <h2>Inscrição confirmada!</h2>
            <p>
              Recebemos seus dados. Em breve você recebe a confirmação no e-mail e no
              WhatsApp informados. Nos vemos em Blumenau.
            </p>
          </div>
        </div>
      </section>
    )
  }

  return (
    /* FORA CLARO, FORMULÁRIO AZUL (12/08, PDF do cliente): "o bloco do garanta
       seu ingresso deve ser branco e o de preenchimento do formulário manter
       azul".

       A dobra inteira vira `.dia`; o painel do formulário volta a ser `.noite`
       LOCALMENTE, o que devolve os tokens escuros só para dentro dele. É o
       mesmo mecanismo que a página já usa para alternar chão — nenhum valor de
       cor é escrito à mão aqui.

       O contraste entre os dois é o que faz o formulário parecer o objeto da
       dobra, e não mais uma caixa sobre o mesmo navy de sempre. */
    <section className="secao secao-inscricao dia dobra-clara" id="inscricao">
      <div className="container-site">
        <div className="cabecalho-secao">
          <span className="eyebrow">Inscrição</span>
          <h2>{chamada}</h2>
          <p>{subtitulo}</p>
        </div>

        <div className="inscricao-conteudo">
          <form className="formulario formulario-painel noite" onSubmit={enviar} noValidate>
            <Campo id="nome" rotulo="Nome completo" erro={erros.nome}>
              <input
                id="nome"
                name="nome"
                type="text"
                autoComplete="name"
                value={dados.nome}
                onChange={mudar('nome')}
                data-erro={Boolean(erros.nome)}
              />
            </Campo>

            <Campo id="email" rotulo="E-mail" erro={erros.email}>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={dados.email}
                onChange={mudar('email')}
                data-erro={Boolean(erros.email)}
              />
            </Campo>

            <Campo id="whatsapp" rotulo="WhatsApp com DDD" erro={erros.whatsapp}>
              {/* +55 fixo fora do campo: o Brasil é o único país aceito no
                  evento, então o código do país nunca é uma escolha da pessoa
                  — é dado, não digitado. `aria-hidden` porque o rótulo do
                  campo já diz "com DDD" e o valor completo (com +55) é lido
                  de volta pra ela na confirmação por e-mail/WhatsApp. */}
              <div className="campo-tel">
                <span className="campo-tel-prefixo" aria-hidden="true">+55</span>
                <input
                  id="whatsapp"
                  name="whatsapp"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  placeholder="(47) 99999-9999"
                  value={dados.whatsapp}
                  onChange={mudar('whatsapp')}
                  data-erro={Boolean(erros.whatsapp)}
                />
              </div>
            </Campo>

            {/* CPF pedido em 12/08. Ele existe para o CREDENCIAMENTO: a lista
                é conferida por documento na porta, e sem ele a conferência
                depende do nome, que duplica e se escreve de N formas. */}
            <Campo id="cpf" rotulo="CPF" erro={erros.cpf}>
              <input
                id="cpf"
                name="cpf"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="000.000.000-00"
                value={dados.cpf}
                onChange={mudar('cpf')}
                data-erro={Boolean(erros.cpf)}
              />
            </Campo>

            <Campo id="imobiliaria" rotulo="Nome da imobiliária" erro={erros.imobiliaria}>
              <input
                id="imobiliaria"
                name="imobiliaria"
                type="text"
                autoComplete="organization"
                value={dados.imobiliaria}
                onChange={mudar('imobiliaria')}
                data-erro={Boolean(erros.imobiliaria)}
              />
            </Campo>

            <Campo id="cargo" rotulo="Qual é seu cargo?" erro={erros.cargo}>
              <Selecao
                id="cargo"
                name="cargo"
                valor={dados.cargo}
                aoMudar={mudar('cargo')}
                opcoes={OPCOES_CARGO}
                erro={erros.cargo}
              />
            </Campo>

            <Campo id="equipe" rotulo="Tamanho da sua equipe?" erro={erros.equipe}>
              <Selecao
                id="equipe"
                name="equipe"
                valor={dados.equipe}
                aoMudar={mudar('equipe')}
                opcoes={OPCOES_EQUIPE}
                erro={erros.equipe}
              />
            </Campo>

            {/* SEM `largo` (13/08, pedido do cliente: "diminua o tamanho desse
                'já trabalha com locação' e deixa ele ao lado do tamanho da
                equipe"). Ele ocupava a linha inteira sozinho porque era o
                último campo antes dos aceites — e, com o almoço fora do
                formulário, isso deixava um select de 700px ao lado de nada.
                Como par do "tamanho da equipe" ele fecha a grade em quatro
                linhas de dois. */}
            <Campo id="locacao" rotulo="Já trabalha com locação?" erro={erros.locacao}>
              <Selecao
                id="locacao"
                name="locacao"
                valor={dados.locacao}
                aoMudar={mudar('locacao')}
                opcoes={OPCOES_LOCACAO}
                erro={erros.locacao}
              />
            </Campo>

            {/* O ALMOÇO VOLTOU PARA DENTRO DO FORMULÁRIO (13/08, pedido do
                cliente: "no formulário de preenchimento deveria ter essa seção
                também, falando sobre o almoço, onde ele deve dar sim ou não").

                ⚠️ ISTO ENCERRA A SEGUNDA ETAPA. O modal existia só para fazer
                esta pergunta depois do envio; com ela aqui, ele não tem mais
                função e sai junto — "Garantir minha presença" volta a ENVIAR o
                formulário, em vez de abrir uma caixa.

                O que motivou o modal em 12/08 continua verdade: esta é a única
                pergunta que não decide a vaga, e junto das outras ela competia
                por atenção arrastando um cardápio de doze itens. A diferença é
                que o cardápio não está mais aqui — ele vive no painel ao lado
                desde 12/08 à noite. Sobra a pergunta sozinha, que é o que o
                cliente está pedindo e cabe em três linhas. */}
            <fieldset className="campo campo-largo campo-almoco">
              <legend className="campo-rotulo">Você quer almoçar no evento?</legend>
              <p className="campo-almoco-nota">
                Opcional e pago no dia, no local. Valores e cardápio ao lado.
              </p>
              <div className="opcoes-radio">
                {[
                  { valor: 'sim', texto: 'Sim, quero o almoço' },
                  { valor: 'nao', texto: 'Não, obrigado' },
                ].map((opcao) => (
                  <label key={opcao.valor} className="opcao-radio">
                    <input
                      type="radio"
                      name="almoco"
                      value={opcao.valor}
                      checked={dados.almoco === opcao.valor}
                      onChange={mudar('almoco')}
                      data-erro={Boolean(erros.almoco)}
                    />
                    <span>{opcao.texto}</span>
                  </label>
                ))}
              </div>
              {erros.almoco && <p className="campo-erro">{erros.almoco}</p>}
            </fieldset>

            <div className="campo campo-largo campo-aceite">
              <label className="opcao-checkbox">
                <input
                  type="checkbox"
                  checked={dados.lgpd}
                  onChange={mudar('lgpd')}
                  data-erro={Boolean(erros.lgpd)}
                />
                <span>{textoLgpd}</span>
              </label>
              {erros.lgpd && <p className="campo-erro">{erros.lgpd}</p>}
            </div>

            <div className="campo campo-largo campo-aceite">
              <label className="opcao-checkbox">
                <input
                  type="checkbox"
                  checked={dados.imagemVoz}
                  onChange={mudar('imagemVoz')}
                  data-erro={Boolean(erros.imagemVoz)}
                />
                <span>
                  Li e aceito os{' '}
                  {/* BOTÃO, não link para outra página: abrir uma aba nova no
                      meio de um formulário preenchido é o caminho mais curto
                      para a pessoa não voltar. O texto do termo continua tendo
                      endereço próprio (/termos-de-uso-de-imagem/) para quem
                      precisar citar ou guardar — os dois leem o MESMO
                      componente. */}
                  <button
                    type="button"
                    className="link-texto"
                    onClick={() => setTermosAbertos(true)}
                  >
                    termos de uso de imagem e voz
                  </button>
                  .
                </span>
              </label>
              {erros.imagemVoz && <p className="campo-erro">{erros.imagemVoz}</p>}
            </div>

            <div className="campo-largo formulario-acao">
              {/* Este botão NÃO ENVIA: valida os campos visíveis e abre o
                  modal do almoço, que é quem envia. O rótulo continua sendo o
                  mesmo CTA da página inteira porque, do ponto de vista de quem
                  clica, é o mesmo ato — a segunda etapa é uma pergunta, não
                  uma nova decisão.

                  (O comentário anterior aqui defendia um botão ÂMBAR. O âmbar
                  saiu de todos os CTAs em 12/08, junto com as lâminas: não há
                  um único botão quente na arte aprovada.) */}
              <button type="submit" className="btn btn-rota" ref={botaoEnviarRef} disabled={estado === 'enviando'}>
                <span>{estado === 'enviando' ? 'Enviando…' : textoCta}</span>
                <span className="btn-seta" aria-hidden="true">→</span>
              </button>
              {estado === 'erro' && (
                <p className="formulario-falha" role="alert">
                  {mensagemErro}
                </p>
              )}
            </div>
          </form>

          {/* O PAINEL DO ALMOÇO SAIU DA LATERAL em 12/08 e foi para dentro
              de `ModalAlmoco`, junto da pergunta. Ele descrevia valor e
              cardápio ao lado de um formulário onde a pergunta correspondente
              já não está mais — informação órfã, ocupando a coluna inteira com
              doze itens de cardápio numa dobra cujo trabalho é converter. */}
          {/* O NAUTA SAIU DAQUI e o painel do almoço VOLTOU (12/08, PDF do
              cliente): "retirar o nauta que fica perdido", "adicionar o prato
              de comida e aumentar a letra pois mal dá para ler".

              Ele estava certo nos dois pontos. O mascote ocupava a coluna
              inteira ao lado de um formulário de dez campos sem dizer nada
              sobre a inscrição, e a informação que a pessoa realmente procura
              nesse momento — quanto custa o almoço, o que tem no cardápio —
              tinha ido para dentro do modal, onde só aparece DEPOIS de o
              formulário estar preenchido.

              A pergunta continua no modal (é lá que ela é respondida e
              enviada); o que volta para cá é a INFORMAÇÃO, que é o que a
              referência do PDF mostra ao lado do formulário. Para não dizer a
              mesma coisa duas vezes, o cardápio agora vive só aqui — o modal
              ficou com a pergunta, o valor e a nota de cobrança. */}
          <aside className="inscricao-lateral">
            <div className="almoco-painel cartao">
              <p className="almoco-painel-titulo">
                <span className="almoco-painel-icone" aria-hidden="true">
                  <IconeTalher />
                </span>
                Almoço no local, por adesão
              </p>

              {/* SLOT DA FOTO DO PRATO. O arquivo pedido no PDF não veio no
                  material entregue; sem ele o painel fecha sem a imagem, e
                  nada quebra. Basta apontar `EVENTO.almocoFoto` para o
                  arquivo quando ele chegar. */}
              {EVENTO.almocoFoto && (
                <img
                  className="almoco-painel-foto"
                  src={EVENTO.almocoFoto}
                  alt="Prato servido no almoço do evento"
                  width="640"
                  height="420"
                  loading="lazy"
                />
              )}

              <p className="almoco-valores">
                <span><strong>R$ 59,90</strong> no PIX</span>
                <span><strong>R$ 64,90</strong> no cartão</span>
              </p>

              <p className="almoco-painel-texto">{textoAlmoco}</p>

              {cardapio && (
                <div className="almoco-cardapio">
                  <p className="almoco-cardapio-titulo">Cardápio · {cardapio.titulo}</p>
                  {cardapio.grupos.map((grupo) => (
                    <div key={grupo.nome} className="almoco-grupo">
                      <h4>{grupo.nome}</h4>
                      <ul className="lista-limpa">
                        {grupo.itens.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      {/* O MODAL DO ALMOÇO SAIU (13/08). A pergunta voltou para dentro do
          formulário a pedido do cliente, e uma caixa que só existia para
          fazê-la não tem mais o que perguntar. O componente foi removido
          junto; o histórico de por que ele existiu está no comentário do
          fieldset, lá em cima. */}

      <ModalTermos aberto={termosAbertos} aoFechar={() => setTermosAbertos(false)} />
    </section>
  )
}

/**
 * Modal de conteúdo — usado pelo termo de uso de imagem e voz. Sem
 * biblioteca: é uma caixa com um texto e um botão de fechar.
 *
 * O "prendedor de foco" é deliberadamente simples: qualquer foco que caia fora
 * da caixa volta para o primeiro elemento focável dela. Não reordena o ciclo
 * Tab inteiro — só garante que o foco nunca ESCAPA para o formulário escondido
 * atrás do véu, que é o requisito de acessibilidade que importa aqui.
 */
function ModalTermos({ aberto, aoFechar }) {
  const caixaRef = useRef(null)

  useEffect(() => {
    if (!aberto) return undefined

    const aoTeclar = (evento) => {
      if (evento.key === 'Escape') aoFechar()
    }
    const aoFocar = (evento) => {
      const caixa = caixaRef.current
      if (caixa && !caixa.contains(evento.target)) {
        caixa.querySelector('button, a, [tabindex]')?.focus()
      }
    }

    document.addEventListener('keydown', aoTeclar)
    document.addEventListener('focusin', aoFocar)
    /* Trava a rolagem do fundo: sem isto, rolar dentro do modal no celular
       arrasta a página inteira por trás dele. */
    const overflowAnterior = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    caixaRef.current?.querySelector('button')?.focus()

    return () => {
      document.removeEventListener('keydown', aoTeclar)
      document.removeEventListener('focusin', aoFocar)
      document.body.style.overflow = overflowAnterior
    }
  }, [aberto, aoFechar])

  if (!aberto) return null

  return (
    <div
      className="modal-veu"
      onClick={(evento) => {
        if (evento.target === evento.currentTarget) aoFechar()
      }}
    >
      <div
        className="modal-caixa modal-caixa-termos"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-termos-titulo"
        ref={caixaRef}
      >
        <button type="button" className="modal-fechar" onClick={aoFechar} aria-label="Fechar">
          ×
        </button>

        <h3 id="modal-termos-titulo">Termo de uso de imagem e voz</h3>

        <div className="modal-corpo">
          <TermosImagem />
        </div>

        <div className="modal-acoes">
          <button type="button" className="btn" onClick={aoFechar}>
            Entendi
          </button>
        </div>
      </div>
    </div>
  )
}


function Campo({ id, rotulo, erro, largo = false, children }) {
  return (
    <div className={`campo ${largo ? 'campo-largo' : ''}`}>
      <label className="campo-rotulo" htmlFor={id}>
        {rotulo}
      </label>
      {children}
      {erro && (
        <p className="campo-erro" id={`${id}-erro`}>
          {erro}
        </p>
      )}
    </div>
  )
}

function Selecao({ id, valor, aoMudar, opcoes, erro }) {
  return (
    <select id={id} value={valor} onChange={aoMudar} data-erro={Boolean(erro)}>
      <option value="">Selecione…</option>
      {opcoes.map((opcao) => (
        <option key={opcao} value={opcao}>
          {opcao}
        </option>
      ))}
    </select>
  )
}
